import { supabase } from '../../../shared/lib/supabase';
import type { Entry, DataEntry, TextContent, TableContent } from '../../../shared/lib/supabase';

const getCurrentUserId = async (): Promise<string> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  return user.id;
};

// Text Entry CRUD Operations

export const listEntries = async (projectId?: string): Promise<Entry[]> => {
  let query = supabase
    .from('entries')
    .select('*')
    .order('position', { ascending: true });
  
  if (projectId) {
    query = query.eq('project_id', projectId);
  }
  
  const { data, error } = await query;

  if (error) {
    console.error('Error fetching entries:', error);
    throw new Error(`Failed to fetch entries: ${error.message}`);
  }

  return data || [];
};

export const getEntry = async (entryId: string): Promise<Entry | null> => {
  const { data, error } = await supabase
    .from('entries')
    .select('*')
    .eq('id', entryId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Entry not found
    }
    console.error('Error fetching entry:', error);
    throw new Error(`Failed to fetch entry: ${error.message}`);
  }

  return data;
};

export const createEntry = async (params: {
  name: string;
  project_id: string;
  content?: TextContent;
  position?: number;
}): Promise<string> => {
  const userId = await getCurrentUserId();
  
  // If no position provided, get the max position and add 1
  let position = params.position;
  if (position === undefined) {
    const { data: entries } = await supabase
      .from('entries')
      .select('position')
      .eq('project_id', params.project_id)
      .order('position', { ascending: false })
      .limit(1);
    
    position = entries && entries.length > 0 ? entries[0].position + 1 : 0;
  }
  
  const defaultContent: TextContent = {
    type: 'rich_text',
    html: '',
    metadata: {
      version: 1,
      created_with: 'AnthroBatch'
    }
  };
  
  const { data, error } = await supabase
    .from('entries')
    .insert({
      name: params.name,
      project_id: params.project_id,
      user_id: userId,
      content: params.content || defaultContent,
      word_count: 0,
      position
    })
    .select('id')
    .single();

  if (error) {
    console.error('Error creating entry:', error);
    throw new Error(`Failed to create entry: ${error.message}`);
  }

  return data.id;
};

export const updateEntry = async (
  entryId: string, 
  updates: Partial<{
    name: string;
    content: TextContent;
    position: number;
  }>
): Promise<void> => {
  // Calculate word count if content is being updated
  let updateData: any = { ...updates };
  if (updates.content) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = updates.content.html;
    const text = tempDiv.textContent || '';
    updateData.word_count = text.split(/\s+/).filter(word => word.length > 0).length;
  }
  
  updateData.updated_at = new Date().toISOString();
  updateData.updated_by = await getCurrentUserId();
  
  const { error } = await supabase
    .from('entries')
    .update(updateData)
    .eq('id', entryId);

  if (error) {
    console.error('Error updating entry:', error);
    throw new Error(`Failed to update entry: ${error.message}`);
  }
};

export const deleteEntry = async (entryId: string): Promise<void> => {
  const { error } = await supabase
    .from('entries')
    .delete()
    .eq('id', entryId);

  if (error) {
    console.error('Error deleting entry:', error);
    throw new Error(`Failed to delete entry: ${error.message}`);
  }
};

// Data Entry CRUD Operations

export const listDataEntries = async (projectId?: string): Promise<DataEntry[]> => {
  let query = supabase
    .from('data_entries')
    .select('*')
    .order('position', { ascending: true });
  
  if (projectId) {
    query = query.eq('project_id', projectId);
  }
  
  const { data, error } = await query;

  if (error) {
    console.error('Error fetching data entries:', error);
    throw new Error(`Failed to fetch data entries: ${error.message}`);
  }

  return data || [];
};

export const getDataEntry = async (entryId: string): Promise<DataEntry | null> => {
  const { data, error } = await supabase
    .from('data_entries')
    .select('*')
    .eq('id', entryId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Entry not found
    }
    console.error('Error fetching data entry:', error);
    throw new Error(`Failed to fetch data entry: ${error.message}`);
  }

  return data;
};

export const createDataEntry = async (params: {
  name: string;
  project_id: string;
  content?: TableContent;
  position?: number;
}): Promise<string> => {
  const userId = await getCurrentUserId();
  
  // If no position provided, get the max position and add 1
  let position = params.position;
  if (position === undefined) {
    const { data: entries } = await supabase
      .from('data_entries')
      .select('position')
      .eq('project_id', params.project_id)
      .order('position', { ascending: false })
      .limit(1);
    
    position = entries && entries.length > 0 ? entries[0].position + 1 : 0;
  }
  
  const defaultContent: TableContent = {
    type: 'data_table',
    notes: '',
    tables: [],
    metadata: {
      table_count: 0,
      version: 1
    }
  };
  
  const { data, error } = await supabase
    .from('data_entries')
    .insert({
      name: params.name,
      project_id: params.project_id,
      user_id: userId,
      content: params.content || defaultContent,
      position
    })
    .select('id')
    .single();

  if (error) {
    console.error('Error creating data entry:', error);
    throw new Error(`Failed to create data entry: ${error.message}`);
  }

  return data.id;
};

export const updateDataEntry = async (
  entryId: string, 
  updates: Partial<{
    name: string;
    content: TableContent;
    position: number;
  }>
): Promise<DataEntry | null> => {
  try {
    const updateData = {
      ...updates,
      updated_at: new Date().toISOString(),
      updated_by: await getCurrentUserId()
    };

    const { data, error } = await supabase
      .from('data_entries')
      .update(updateData)
      .eq('id', entryId)
      .select()
      .single();

    if (error) {
      console.error('Error updating data entry:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateDataEntry:', error);
    throw error;
  }
};

export const deleteDataEntry = async (entryId: string): Promise<void> => {
  const { error } = await supabase
    .from('data_entries')
    .delete()
    .eq('id', entryId);

  if (error) {
    console.error('Error deleting data entry:', error);
    throw new Error(`Failed to delete data entry: ${error.message}`);
  }
};

// Enhanced data entry operations with real-time support
export const updateDataEntryWithRealtime = async (
  id: string, 
  updates: Partial<DataEntry>,
  optimistic: boolean = false
): Promise<DataEntry | null> => {
  try {
    const updateData = {
      ...updates,
      updated_at: new Date().toISOString(),
      updated_by: await getCurrentUserId()
    };

    const { data, error } = await supabase
      .from('data_entries')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating data entry:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateDataEntryWithRealtime:', error);
    throw error;
  }
};

// Real-time specific functions for data entries
export const createDataEntrySnapshot = async (
  entryId: string,
  content: TableContent,
  version: number
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('data_entry_snapshots')
      .insert({
        entry_id: entryId,
        content,
        version,
        created_by: await getCurrentUserId(),
        created_at: new Date().toISOString()
      });

    if (error) {
      console.error('Error creating snapshot:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in createDataEntrySnapshot:', error);
    throw error;
  }
};

export const getDataEntrySnapshots = async (entryId: string, limit: number = 10): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('data_entry_snapshots')
      .select('*')
      .eq('entry_id', entryId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching snapshots:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getDataEntrySnapshots:', error);
    return [];
  }
};

// Conflict resolution helpers for data entries
export const resolveTableConflicts = (
  localContent: TableContent,
  remoteContent: TableContent,
  conflictResolution: 'local' | 'remote' | 'merge' = 'merge'
): TableContent => {
  if (conflictResolution === 'local') {
    return localContent;
  }
  
  if (conflictResolution === 'remote') {
    return remoteContent;
  }

  // Merge strategy: combine tables from both versions
  const mergedTables = [...localContent.tables];
  
  // Add remote tables that don't exist locally
  remoteContent.tables.forEach(remoteTable => {
    const localTable = mergedTables.find(t => t.id === remoteTable.id);
    if (!localTable) {
      mergedTables.push(remoteTable);
    }
  });

  return {
    type: 'data_table',
    notes: remoteContent.notes || localContent.notes, // Prefer remote notes
    tables: mergedTables,
    metadata: {
      table_count: mergedTables.length,
      version: Math.max(localContent.metadata.version, remoteContent.metadata.version) + 1
    }
  };
};

// Optimistic update helpers for data entries
export const applyOptimisticUpdate = (
  currentContent: TableContent,
  update: Partial<TableContent>
): TableContent => {
  return {
    ...currentContent,
    ...update,
    metadata: {
      ...currentContent.metadata,
      ...update.metadata,
      version: currentContent.metadata.version + 1
    }
  };
};

export const rollbackOptimisticUpdate = (
  currentContent: TableContent,
  rollbackToVersion: number
): TableContent => {
  return {
    ...currentContent,
    metadata: {
      ...currentContent.metadata,
      version: rollbackToVersion
    }
  };
};

// Search functions

export const searchEntries = async (query: string, projectId?: string): Promise<Entry[]> => {
  let searchQuery = supabase
    .from('entries')
    .select('*')
    .or(`name.ilike.%${query}%,content->html.ilike.%${query}%`)
    .order('updated_at', { ascending: false });
  
  if (projectId) {
    searchQuery = searchQuery.eq('project_id', projectId);
  }
  
  const { data, error } = await searchQuery;

  if (error) {
    console.error('Error searching entries:', error);
    throw new Error(`Failed to search entries: ${error.message}`);
  }

  return data || [];
};

export const searchDataEntries = async (query: string, projectId?: string): Promise<DataEntry[]> => {
  let searchQuery = supabase
    .from('data_entries')
    .select('*')
    .ilike('name', `%${query}%`)
    .order('updated_at', { ascending: false });
  
  if (projectId) {
    searchQuery = searchQuery.eq('project_id', projectId);
  }
  
  const { data, error } = await searchQuery;

  if (error) {
    console.error('Error searching data entries:', error);
    throw new Error(`Failed to search data entries: ${error.message}`);
  }

  return data || [];
};

// Statistics functions

export const getEntriesStats = async (projectId?: string) => {
  // Get text entry stats
  let textQuery = supabase
    .from('entries')
    .select('id, word_count');
  
  if (projectId) {
    textQuery = textQuery.eq('project_id', projectId);
  }
  
  // Get data entry stats
  let dataQuery = supabase
    .from('data_entries')
    .select('id');
  
  if (projectId) {
    dataQuery = dataQuery.eq('project_id', projectId);
  }
  
  const [textResult, dataResult] = await Promise.all([
    textQuery,
    dataQuery
  ]);
  
  if (textResult.error || dataResult.error) {
    console.error('Error fetching stats:', textResult.error || dataResult.error);
    return {
      textEntries: 0,
      dataEntries: 0,
      totalWords: 0
    };
  }
  
  const textEntries = textResult.data || [];
  const dataEntries = dataResult.data || [];
  
  return {
    textEntries: textEntries.length,
    dataEntries: dataEntries.length,
    totalWords: textEntries.reduce((sum, entry) => sum + (entry.word_count || 0), 0)
  };
};

// Month-based calendar functions for performance optimization
const getMonthRange = (year: number, month: number) => {
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return {
    start: startDate.toISOString(),
    end: endDate.toISOString()
  };
};

// Get entries for a specific month
export const getEntriesForMonth = async (year: number, month: number) => {
  const userId = await getCurrentUserId();
  const { start, end } = getMonthRange(year, month);
  
  const { data: entries, error } = await supabase
    .from('entries')
    .select('id, name, created_at, project_id')
    .eq('created_by', userId)
    .gte('created_at', start)
    .lte('created_at', end)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching entries for month:', error);
    throw new Error(`Failed to fetch entries: ${error.message}`);
  }

  return entries || [];
};

// Get data entries for a specific month
export const getDataEntriesForMonth = async (year: number, month: number) => {
  const userId = await getCurrentUserId();
  const { start, end } = getMonthRange(year, month);
  
  const { data: dataEntries, error } = await supabase
    .from('data_entries')
    .select('id, name, created_at, project_id')
    .eq('created_by', userId)
    .gte('created_at', start)
    .lte('created_at', end)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching data entries for month:', error);
    throw new Error(`Failed to fetch data entries: ${error.message}`);
  }

  return dataEntries || [];
};